<template>
  <v-container>
    <div class="header d-print-none">
      <div class="d-print-none d-flex justify-space-between">
        <date-range-picker mode="range" v-model="dateRange" class="mx-2" />
        <v-btn @click="print" icon>
          <v-icon x-large>mdi-printer</v-icon>
        </v-btn>
      </div>

      <div class="d-flex justify-space-between d-print-none">
        <v-select
          class="mr-4"
          v-model="groupBy"
          :items="['Assignment', 'Time']"
          label="Group By"
        ></v-select>

        <v-select
          class="ml-4"
          v-model="groupLevel"
          :items="['Hour', 'Day', 'Week']"
          label="Group By Level"
        ></v-select>
      </div>

      <div class="d-flex d-print-none">
        <v-checkbox
          v-model="showTeacherAssignments"
          label="Show Private Assignments"
        ></v-checkbox>
        <v-checkbox
          class="ml-4"
          v-model="showClassroomAssignments"
          label="Show Classroom Assignments"
        ></v-checkbox>
      </div>

      <div class="text-center ma-4 d-print-none" v-if="!groupedSchedules">
        <v-btn color="primary" @click="reloadData">Reload</v-btn>
      </div>

      <v-divider class="mb-4 d-print-none" />
    </div>

    <!-- Print student schedules based on grouping. By Teacher:Date:Time or Date:Time:Teacher -->
    <div
      v-for="item in groupedSchedulesDisplay"
      :key="item.key"
      class="print-section mt-4"
    >
      <h4>{{ item.label }}</h4>
      <hr class="mb-4" />

      <table class="table-container"  cellspacing="20px" cellpadding="0">
        <tr
          v-for="(subItemList, index) in chunk(item.children, columnCount)"
          :key="index"
        >
          <td
            v-for="subItem in subItemList"
            :key="subItem.key"
            class="break-inside-avoid column"
          >
            <div class="column-cell">
              <div class="subItem-label">{{ subItem.label }}</div>
              <hr />

              <ul class="list-group">
                <li
                  v-for="schedule in subItem.schedules"
                  :key="schedule ? schedule.scheduleId : 0"
                  class="list-group-item"
                >
                  <div v-if="schedule">
                    {{ schedule.studentFirstName }}
                    {{ schedule.studentLastName }}
                    <div class="text-caption">
                      {{ getProgramNameById(schedule.programId) }}
                    </div>
                    <div
                      v-if="groupLevel == 'Week' || groupLevel == 'Day'"
                      class="text-caption"
                    >
                      <span v-if="groupLevel == 'Week'">{{
                        schedule.startTime | formatDate
                      }}</span>
                      {{ schedule.startTime | formatTime }} -
                      {{ schedule.endTime | formatTime }}
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </td>
        </tr>
      </table>
    </div>
  </v-container>
</template>

<script>
import { fetchStaffAndStudentSchedules } from "@/services/schedulingService.js";
import { mapGetters } from "vuex";
import DateRangePicker from "@/components/common/DateRangePicker.vue";
import moment from "moment";

export default {
  data: () => ({
    groupBy: "Assignment",
    groupLevel: "Hour",
    dateRange: {
      start: moment().startOf("day"),
      end: moment().add(7, "days").endOf("day"),
    },
    studentSchedules: [],
    teachers: {},
    students: {},
    classrooms: {},
    groupedSchedules: {},
    programs: [],
    showTeacherAssignments: true,
    showClassroomAssignments: true,
    columnCount: 4,
  }),

  computed: {
    ...mapGetters(["globalCenter"]),

    groupedSchedulesDisplay() {
      if (this.groupedSchedules) {
        return this.transformGroupedSchedulesIntoDisplayFormat(
          this.groupedSchedules
        );
      } else {
        return null;
      }
    },
  },

  components: { DateRangePicker },

  watch: {
    dateRange: {
      handler() {
        this.groupedSchedules = null;
      },
      deep: true,
    },
    groupBy() {
      this.groupSchedules();
    },
    groupLevel() {
      this.groupSchedules();
    },
    showTeacherAssignments() {
      this.groupSchedules();
    },
    showClassroomAssignments() {
      this.groupSchedules();
    },
  },

  async created() {

    if(this.$route.query.start){
      this.dateRange.start = this.$route.query.start;
    }
    
    if(this.$route.query.end){
      this.dateRange.end = this.$route.query.end;
    }
    

    await this.loadPrograms();
    this.fetchSchedules();
  },

  methods: {
    reloadData() {
      this.fetchSchedules();
    },
    chunk(arr, chunkSize) {
      var R = [];
      for (var i = 0, len = arr.length; i < len; i += chunkSize) {
        R.push(arr.slice(i, i + chunkSize));
      }
      return R;
    },
    async fetchSchedules() {
      const centerId = this.globalCenter.id;

      const data = await fetchStaffAndStudentSchedules(
        centerId,
        this.dateRange.start,
        this.dateRange.end
      );

      this.studentSchedules = data.groupedData
        .map((item) => item.students)
        .flat();

      this.teachers = Object.fromEntries(
        data.teachers.map((teacher) => [teacher.id, teacher])
      );
      this.classrooms = Object.fromEntries(
        data.classrooms.map((classroom) => [classroom.id, classroom])
      );
      this.students = Object.fromEntries(
        data.students.map((student) => [student.id, student])
      );

      this.groupSchedules();
    },

    groupSchedules() {
      if (this.groupBy === "Time") {
        if (this.groupLevel === "Hour") {
          this.groupedByTimeHour();
        } else if (this.groupLevel === "Day") {
          this.groupedByTimeDay();
        } else if (this.groupLevel === "Week") {
          this.groupedByTimeWeek();
        } else {
          throw new Error(
            `Unknown group level ${this.groupLevel} for group by Time`
          );
        }
      } else if (this.groupBy === "Assignment") {
        if (this.groupLevel === "Hour") {
          this.groupedByAssignmentHour();
        } else if (this.groupLevel === "Day") {
          this.groupedByAssignmentDay();
        } else if (this.groupLevel === "Week") {
          this.groupedByAssignmentWeek();
        } else {
          throw new Error(
            `Unknown group level ${this.groupLevel} for group by Assignment`
          );
        }
      } else {
        throw new Error(`Unknown group by ${this.groupBy}`);
      }
    },

    // Group By Assignment (either teacher or classroom)
    groupedByAssignmentHour() {
      this.groupedSchedules = this.studentSchedules.reduce((acc, schedule) => {
        const startTime = moment(schedule.startTime).format(
          "YYYY-MM-DD, hh:mm A"
        );
        const endTime = moment(schedule.endTime).format("hh:mm A");

        let assignment = "Unassigned";

        if (
          this.showTeacherAssignments &&
          (schedule.teacherId || schedule.tempTeacherId)
        ) {
          let teacherId = schedule.teacherId || schedule.tempTeacherId;
          if (teacherId && this.teachers[teacherId]) {
            assignment = this.teachers[teacherId].firstName;
          }
        } else if (
          this.showClassroomAssignments &&
          (schedule.classroomId || schedule.tempClassroomId)
        ) {
          let classroomId = schedule.classroomId || schedule.tempClassroomId;
          if (classroomId && this.classrooms[classroomId]) {
            assignment = this.classrooms[classroomId].roomNumber;
          }
        }

        if (!acc[assignment]) {
          acc[assignment] = {};
        }

        const timeSlot = `${startTime} - ${endTime}`; // Include start date in time slot

        if (!acc[assignment][timeSlot]) {
          acc[assignment][timeSlot] = [];
        }

        acc[assignment][timeSlot].push(schedule);
        return acc;
      }, {});
    },

    groupedByAssignmentDay() {
      this.groupedSchedules = this.studentSchedules.reduce((acc, schedule) => {
        const date = moment(schedule.startTime).format("YYYY-MM-DD");

        let assignment = "Unassigned";

        if (
          this.showTeacherAssignments &&
          (schedule.teacherId || schedule.tempTeacherId)
        ) {
          let teacherId = schedule.teacherId || schedule.tempTeacherId;
          if (teacherId && this.teachers[teacherId]) {
            assignment = this.teachers[teacherId].firstName;
          }
        } else if (
          this.showClassroomAssignments &&
          (schedule.classroomId || schedule.tempClassroomId)
        ) {
          let classroomId = schedule.classroomId || schedule.tempClassroomId;
          if (classroomId && this.classrooms[classroomId]) {
            assignment = this.classrooms[classroomId].roomNumber;
          }
        }

        if (!acc[assignment]) {
          acc[assignment] = {};
        }

        if (!acc[assignment][date]) {
          acc[assignment][date] = [];
        }

        acc[assignment][date].push(schedule);
        return acc;
      }, {});
    },

    groupedByAssignmentWeek() {
      this.groupedSchedules = this.studentSchedules.reduce((acc, schedule) => {
        const weekStart = moment(schedule.startTime)
          .startOf("week")
          .format("YYYY-MM-DD");

        let assignment = "Unassigned";

        if (
          this.showTeacherAssignments &&
          (schedule.teacherId || schedule.tempTeacherId)
        ) {
          let teacherId = schedule.teacherId || schedule.tempTeacherId;
          if (teacherId && this.teachers[teacherId]) {
            assignment = this.teachers[teacherId].firstName;
          }
        } else if (
          this.showClassroomAssignments &&
          (schedule.classroomId || schedule.tempClassroomId)
        ) {
          let classroomId = schedule.classroomId || schedule.tempClassroomId;
          if (classroomId && this.classrooms[classroomId]) {
            assignment = this.classrooms[classroomId].roomNumber;
          }
        }

        if (!acc[assignment]) {
          acc[assignment] = {};
        }

        if (!acc[assignment][weekStart]) {
          acc[assignment][weekStart] = [];
        }

        acc[assignment][weekStart].push(schedule);
        return acc;
      }, {});
    },

    transformGroupedSchedulesIntoDisplayFormat(groupedSchedules) {
      return Object.entries(groupedSchedules).map(([key, value]) => {
        const children = Object.entries(value).map(([subKey, schedules]) => {
          if (!Array.isArray(schedules)) {
            schedules = [schedules];
          }

          return {
            key: subKey,
            label: subKey,
            students: schedules.map(
              (schedule) => this.students[schedule.studentId]
            ),
            schedules: schedules, // Include the schedule objects directly
          };
        });

        const sortedChildren = children.sort((a, b) => {
          return a.label.localeCompare(b.label);
        });

        return {
          key,
          label: key,
          children: sortedChildren,
        };
      });
    },
    groupedByTimeHour() {
      this.groupedSchedules = this.studentSchedules.reduce((acc, schedule) => {
        const startTime = moment(schedule.startTime).format(
          "YYYY-MM-DD, hh:mm A"
        );
        const endTime = moment(schedule.endTime).format("hh:mm A");
        let group = "Unassigned";

        if (
          this.showTeacherAssignments &&
          (schedule.teacherId || schedule.tempTeacherId)
        ) {
          let teacherId = schedule.teacherId || schedule.tempTeacherId;
          if (teacherId && this.teachers[teacherId]) {
            group = this.teachers[teacherId].firstName;
          }
        } else if (
          this.showClassroomAssignments &&
          (schedule.classroomId || schedule.tempClassroomId)
        ) {
          let classroomId = schedule.classroomId || schedule.tempClassroomId;
          if (classroomId && this.classrooms[classroomId]) {
            group = this.classrooms[classroomId].roomNumber;
          }
        }

        const timeSlot = `${startTime} - ${endTime}`; // Include start date in time slot

        if (!acc[timeSlot]) {
          acc[timeSlot] = {};
        }

        if (!acc[timeSlot][group]) {
          acc[timeSlot][group] = [];
        }

        acc[timeSlot][group].push(schedule);
        return acc;
      }, {});
    },
    groupedByTimeDay() {
      this.groupedSchedules = this.studentSchedules.reduce((acc, schedule) => {
        const date = schedule.startTime.split("T")[0];
        let group = "Unassigned";

        if (
          this.showTeacherAssignments &&
          (schedule.teacherId || schedule.tempTeacherId)
        ) {
          let teacherId = schedule.teacherId || schedule.tempTeacherId;
          if (teacherId && this.teachers[teacherId]) {
            group = this.teachers[teacherId].firstName;
          }
        } else if (
          this.showClassroomAssignments &&
          (schedule.classroomId || schedule.tempClassroomId)
        ) {
          let classroomId = schedule.classroomId || schedule.tempClassroomId;
          if (classroomId && this.classrooms[classroomId]) {
            group = this.classrooms[classroomId].roomNumber;
          }
        }

        if (!acc[date]) {
          acc[date] = {};
        }
        if (!acc[date][group]) {
          acc[date][group] = [];
        }

        acc[date][group].push(schedule);
        return acc;
      }, {});
    },

    groupedByTimeWeek() {
      this.groupedSchedules = this.studentSchedules.reduce((acc, schedule) => {
        const week = `Week ${moment(schedule.startTime).week()}, ${moment(
          schedule.startTime
        ).year()}`;
        let group = "Unassigned";

        if (
          this.showTeacherAssignments &&
          (schedule.teacherId || schedule.tempTeacherId)
        ) {
          let teacherId = schedule.teacherId || schedule.tempTeacherId;
          if (teacherId && this.teachers[teacherId]) {
            group = this.teachers[teacherId].firstName;
          }
        } else if (
          this.showClassroomAssignments &&
          (schedule.classroomId || schedule.tempClassroomId)
        ) {
          let classroomId = schedule.classroomId || schedule.tempClassroomId;
          if (classroomId && this.classrooms[classroomId]) {
            group = this.classrooms[classroomId].roomNumber;
          }
        }

        if (!acc[week]) {
          acc[week] = {};
        }
        if (!acc[week][group]) {
          acc[week][group] = [];
        }

        acc[week][group].push(schedule);
        return acc;
      }, {});
    },
    async loadPrograms(center) {
      const params = { centerId: center };
      await this.$axios
        .get("/program/get-by-center", { params })
        .then((resp) => {
          this.programs = resp.data;
        });
    },
    getProgramNameById(programId) {
      const program = this.programs.find((p) => p.id == programId);
      return program ? program.name : "N/A";
    },
    print() {
      window.print();
    },
  },
};
</script>

<style scoped>
.column {
  vertical-align: top;
  width: 25%;
}
.column-cell {
  max-width: 250px;
}

.print-section {
  page-break-inside: avoid;
}
.break-inside-avoid {
  break-inside: avoid;
}
.table-container {
  width: 100%;
  table-layout: fixed;
}
.text-caption {
  font-size: 0.65em;
  color: #6c757d;
}
.subItem-label {
  font-size: 0.75em;
  font-weight: 500;
}
.list-group {
  list-style-type: none;
  padding-left: 0;
}
.list-group-item {
  padding: 0.25rem 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
  background-color: #fff;
  font-size: 0.75em;
}
.list-group-item:last-child {
  border-bottom: none;
}

@media print {
  .print-section {
    page-break-inside: avoid;
  }
  .break-inside-avoid {
    break-inside: avoid;
  }
  .table-container {
    width: 100%;
    table-layout: fixed;
  }
  .text-caption,
  .list-group-item {
    font-size: 0.6em; /* further decreased font size for print */
  }
  .subItem-label {
    font-size: 0.6em; /* further decreased font size for print */
  }
}
</style>
